// import React, {MutableRefObject, useRef} from 'react';
import React, {MutableRefObject, useEffect, useLayoutEffect, useMemo, useRef} from 'react';

import useWindowSize from '../../utils/hooks/useWindowSize';
import {SetState} from '../../utils/types/Global';
import {AnchorsType} from '../../utils/types/Pages';
import MobileOverlay from './MobileOverlay';
import {motion} from 'framer-motion';
import {pageTransitionDelay} from '../../utils/data/Global';

interface ScrollType {
    id: string;
    children: React.ReactNode;
    pointer: boolean;
    setPointer: SetState<boolean>;
    leaving: boolean;
    anchors?: AnchorsType;
    initialScrollY?: number;
}

const Scroll: React.FC<ScrollType> = ({id, children, anchors, leaving, initialScrollY}) => {
    const windowSize = useWindowSize();
    const scrollingContainerRef = useRef() as MutableRefObject<HTMLDivElement>;

    const noscroll = windowSize.width <= 1200;

    // A back/forward (or in-app "back") navigation flips window.location the
    // instant it starts, well before this (still-mounted, still exit-animating)
    // page's own React state catches up - react-router's <Routes location>
    // actually freezes useLocation() for the exiting tree until it unmounts, so
    // that can't be used here. From the instant the URL changes, window.scrollY
    // reflects the *target* page's history, not ours, so the loop below must
    // stop reading it immediately by checking the raw URL every frame - or it
    // eases this page's content toward a position that belongs to the page
    // we're leaving for.
    const ownPathnameRef = useRef(window.location.pathname);

    useEffect(() => {
        if (!noscroll && !leaving) {
            setBodyHeight();
        } else if (noscroll) {
            document.body.style.height = '';
        }
    }, [windowSize.height, windowSize.width, noscroll, leaving]);

    const setBodyHeight = () => {
        document.body.style.height = `${scrollingContainerRef.current.getBoundingClientRect().height}px`;
    };

    // Keeps the body height in sync with the scrolling content without reading
    // layout on every animation frame (that used to force a reflow at 60fps).
    useEffect(() => {
        if (noscroll || !scrollingContainerRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
            if (!leaving) setBodyHeight();
        });
        resizeObserver.observe(scrollingContainerRef.current);
        return () => resizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noscroll]);

    const data = useMemo(
        () => [
            {
                ease: 0.06,
                current: 0,
                previous: 0,
                rounded: 0,
            },
        ],
        [],
    );

    // Restores the exact scroll position instantly (no easing) when returning
    // to this page via browser back/forward or an in-app "back" link, instead
    // of starting the transform lerp from 0 and letting it visibly catch up.
    useLayoutEffect(() => {
        if (noscroll || !scrollingContainerRef.current) {
            if (typeof initialScrollY === 'number') window.scrollTo(0, initialScrollY);
            return;
        }

        setBodyHeight();

        const seed = typeof initialScrollY === 'number' ? initialScrollY : window.scrollY;
        if (typeof initialScrollY === 'number') {
            window.scrollTo(0, seed);
        }

        data[0].current = seed;
        data[0].previous = seed;
        data[0].rounded = seed;
        scrollingContainerRef.current.style.transform = `translate3d(0px, -${seed}px, 0px)`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The animation loop is written so the effect's cleanup is the only way to
    // stop it: each tick reads `stopped` from the closure before scheduling the
    // next frame, so a resize across the mobile breakpoint (or unmount) can't
    // leave an orphaned loop running forever writing styles in the background.
    useEffect(() => {
        if (noscroll) return;
        let stopped = false;

        const tick = () => {
            if (stopped) return;
            const isNavigatingAway = window.location.pathname !== ownPathnameRef.current;
            if (!isNavigatingAway && scrollingContainerRef.current) {
                data[0].current = window.scrollY;
                data[0].previous += (data[0].current - data[0].previous) * data[0].ease;
                data[0].rounded = Math.round(data[0].previous * 100) / 100;
                scrollingContainerRef.current.style.transform = `translate3d(0px, -${data[0].rounded}px, 0px)`;
            }
            requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
        return () => {
            stopped = true;
        };
    }, [noscroll, data]);

    useEffect(() => {
        if (noscroll && scrollingContainerRef.current) {
            scrollingContainerRef.current.style.transform = '';
            data[0].current = 0;
            data[0].previous = 0;
            data[0].rounded = 0;
        }
    }, [noscroll, data]);

    return (
        <motion.div className="scroll-html" transition={pageTransitionDelay} initial={{pointerEvents: 'none'}} exit={{pointerEvents: 'none'}} animate={{pointerEvents: 'all'}}>
            <div className="scroll-body">
                <div className={'scroll-wrapper ' + (noscroll ? 'noscroll' : '')}>
                    {/* <div className="scroll-wrapper"> */}
                    <div id={id} ref={!leaving ? scrollingContainerRef : null}>
                        <MobileOverlay anchors={anchors} />
                        {children}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default Scroll;
